import React from "react";
import { useTranslation } from "react-i18next";
import { Identifiable, Milestone as M, Project } from "@planzone/dto";
import { UseCases } from "@planzone/core";

import { useModal } from "@root/client/modal";
import { Props } from "./types";
import { Milestone } from "./milestone";
import { Today } from "./today";
import styles from "./styles.scss";

function getDatePosition(project: Identifiable<Project>, date: Date) {
  const start = new Date(project.rootList.startDate).getTime();
  const end = new Date(project.rootList.endDate).getTime();
  const d = date.getTime();

  return ((d - start) / (end - start)) * 100;
}

function isMilestoneLate({ endDate }: Identifiable<M>) {
  return !!endDate && new Date(endDate) < new Date();
}

function isMilestoneDone({ state }: Identifiable<M>) {
  return state === "DONE";
}

function isTodayOutOfScope(project: Identifiable<Project>) {
  const now = new Date();

  return now > new Date(project.rootList.endDate);
}

function Milestones({ milestones, project }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();

  return (
    <div className={styles.milestones}>
      <div className={styles.timeline}>
        <div className={styles.list}>
          {!isTodayOutOfScope(project) && (
            <div
              className={styles.today}
              style={{
                left: `${getDatePosition(project, new Date())}%`,
              }}
            >
              <Today />
            </div>
          )}
          {milestones.map((milestone) => {
            const isDone = isMilestoneDone(milestone);
            const isLate = isMilestoneLate(milestone) && !isDone;
            const percentage = getDatePosition(
              project,
              new Date(isDone ? milestone.completedAt : milestone.endDate),
            );

            return (
              <div
                key={milestone.uuid}
                className={styles.milestone}
                style={{ left: `${percentage}%` }}
                onClick={() =>
                  pushView({
                    key: UseCases.retrieveMilestone,
                    data: { milestone },
                  })
                }
              >
                <Milestone
                  milestone={milestone}
                  project={project}
                  percentage={percentage}
                  isDone={isDone}
                  isLate={isLate}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.dates}>
        <p className={styles.date}>
          {t("projectPage.synthesisPage.monitoring.start")} :{" "}
          {new Date(project.rootList.startDate).toLocaleDateString()}
        </p>
        <p className={styles.date}>
          {t("projectPage.synthesisPage.monitoring.end")} :{" "}
          {new Date(project.rootList.endDate).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

export { Milestones };
